import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  RefreshCw, Download, Settings, Link2, Check, X,
  Building2, Briefcase, Users, History, Zap, Unplug
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL + "/api";

const SyncCard = ({ title, icon: Icon, onSync, syncing, lastResult }) => {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <Icon size={18} />
          {title}
        </h3>
        <button
          data-testid={"sync-" + title.toLowerCase().replace(/\s/g, "-")}
          onClick={onSync}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
        >
          {syncing ? (
            <RefreshCw size={16} className="animate-spin" />
          ) : (
            <Download size={16} />
          )}
          {syncing ? "Syncing..." : "Sync Now"}
        </button>
      </div>
      {lastResult && (
        <div className="flex flex-wrap gap-3 text-sm">
          <span className="px-2 py-1 bg-green-950/50 text-green-400 rounded">
            Created: {lastResult.created}
          </span>
          <span className="px-2 py-1 bg-blue-950/50 text-blue-400 rounded">
            Updated: {lastResult.updated}
          </span>
          {lastResult.errors > 0 && (
            <span className="px-2 py-1 bg-red-950/50 text-red-400 rounded">
              Errors: {lastResult.errors}
            </span>
          )}
          <span className="text-muted-foreground">
            Total: {lastResult.total}
          </span>
        </div>
      )}
    </div>
  );
};

const SyncHistoryRow = ({ log }) => {
  const typeIcons = { companies: Building2, opportunities: Briefcase, contacts: Users };
  const Icon = typeIcons[log.type] || History;
  return (
    <div className="flex items-center gap-4 py-3 border-b border-border last:border-0">
      <Icon size={16} className="text-muted-foreground flex-shrink-0" />
      <span className="text-sm font-medium capitalize w-28">{log.type}</span>
      <span className="text-xs px-2 py-0.5 bg-green-950/50 text-green-400 rounded">+{log.created}</span>
      <span className="text-xs px-2 py-0.5 bg-blue-950/50 text-blue-400 rounded">~{log.updated}</span>
      {log.errors > 0 && (
        <span className="text-xs px-2 py-0.5 bg-red-950/50 text-red-400 rounded">!{log.errors}</span>
      )}
      <span className="text-xs text-muted-foreground ml-auto">
        {new Date(log.completed_at).toLocaleString()}
      </span>
      <span className="text-xs text-muted-foreground">{log.user}</span>
    </div>
  );
};

export const SpiroImportPage = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);
  const [authCode, setAuthCode] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncingCompanies, setSyncingCompanies] = useState(false);
  const [syncingOpps, setSyncingOpps] = useState(false);
  const [syncingContacts, setSyncingContacts] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [companyResult, setCompanyResult] = useState(null);
  const [oppResult, setOppResult] = useState(null);
  const [contactResult, setContactResult] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(API + "/spiro/status");
      setStatus(res.data);
    } catch (err) {
      setStatus({ connected: false, has_tokens: false });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(API + "/spiro/sync-history");
      setSyncHistory(res.data.logs || []);
    } catch (err) {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchHistory();
  }, [fetchStatus, fetchHistory]);

  const handleConnect = async () => {
    if (!authCode.trim()) {
      toast.error("Please paste the authorization code");
      return;
    }
    setConnecting(true);
    try {
      await axios.post(API + "/spiro/auth-callback", { code: authCode.trim() });
      toast.success("Connected to Spiro CRM!");
      setShowConnect(false);
      setAuthCode("");
      setStatus({ connected: true, has_tokens: true });
      fetchHistory();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to connect";
      toast.error(msg);
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect from Spiro CRM?")) return;
    try {
      await axios.post(API + "/spiro/disconnect");
      toast.success("Disconnected from Spiro");
      setStatus({ connected: false, has_tokens: false });
    } catch (err) {
      toast.error("Failed to disconnect");
    }
  };

  const doSync = async (endpoint, setter, resultSetter) => {
    setter(true);
    try {
      const res = await axios.post(API + "/spiro/sync/" + endpoint);
      if (resultSetter) resultSetter(res.data);
      toast.success("Sync complete!");
      await fetchHistory();
    } catch (err) {
      const msg = err.response?.data?.detail || "Sync failed";
      toast.error(msg);
    } finally {
      setter(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const res = await axios.post(API + "/spiro/sync/all");
      const r = res.data.results || {};
      if (r.companies) setCompanyResult(r.companies);
      if (r.opportunities) setOppResult(r.opportunities);
      if (r.contacts) setContactResult(r.contacts);
      toast.success("Full sync complete!");
      await fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading Spiro status...</div>
      </div>
    );
  }

  const isConnected = status?.connected;

  return (
    <div className="space-y-6" data-testid="spiro-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Zap size={28} />
            Spiro CRM Integration
          </h1>
          <p className="text-muted-foreground mt-1">
            Sync companies, opportunities, and contacts directly from Spiro
          </p>
        </div>
        {isConnected && (
          <button
            data-testid="sync-all-btn"
            onClick={handleSyncAll}
            disabled={syncingAll}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50"
          >
            {syncingAll ? (
              <RefreshCw size={18} className="animate-spin" />
            ) : (
              <Download size={18} />
            )}
            {syncingAll ? "Syncing All..." : "Sync All"}
          </button>
        )}
      </div>

      {/* Connection Status */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={
              "w-12 h-12 rounded-full flex items-center justify-center " +
              (isConnected ? "bg-green-950/50" : "bg-secondary")
            }>
              {isConnected ? (
                <Check size={24} className="text-green-400" />
              ) : (
                <X size={24} className="text-muted-foreground" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                {isConnected ? "Connected to Spiro" : "Not Connected"}
              </h2>
              <p className="text-sm text-muted-foreground">
                {isConnected
                  ? "OAuth tokens active. Last refreshed: " + (status.tokens_updated_at
                      ? new Date(status.tokens_updated_at).toLocaleString()
                      : "N/A")
                  : "Complete the OAuth flow to connect your Spiro account"}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {isConnected ? (
              <button
                data-testid="disconnect-btn"
                onClick={handleDisconnect}
                className="flex items-center gap-2 px-4 py-2 bg-red-950/30 text-red-400 rounded-lg text-sm hover:bg-red-950/50"
              >
                <Unplug size={16} />
                Disconnect
              </button>
            ) : (
              <button
                data-testid="connect-btn"
                onClick={() => setShowConnect(!showConnect)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90"
              >
                <Link2 size={16} />
                Connect to Spiro
              </button>
            )}
          </div>
        </div>

        {/* OAuth Connect Flow */}
        {showConnect && !isConnected && (
          <div className="mt-6 pt-6 border-t border-border space-y-4">
            <h3 className="font-semibold flex items-center gap-2">
              <Settings size={18} />
              OAuth Authorization
            </h3>
            <div className="space-y-3">
              <div className="bg-secondary/50 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Step 1: Go to Spiro Settings</p>
                <p className="text-sm text-muted-foreground">
                  In Spiro, go to <strong>Settings &rarr; API Tokens</strong> and find the <strong>DocSync Hub</strong> app.
                </p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4">
                <p className="text-sm font-medium mb-2">Step 2: Click the "Authorize" button</p>
                <p className="text-sm text-muted-foreground">
                  Click <strong>Authorize</strong> next to the app. Enter your Spiro login if prompted.
                  You'll be redirected to a URL like:
                </p>
                <code className="text-xs text-muted-foreground block mt-2">
                  https://localhost/done?code=XXXXXXXX
                </code>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4">
                <p className="text-sm font-medium mb-3">
                  Step 3: Copy the <strong>code</strong> value from that URL and paste it here:
                </p>
                <div className="flex gap-3">
                  <input
                    data-testid="auth-code-input"
                    type="text"
                    value={authCode}
                    onChange={(e) => setAuthCode(e.target.value)}
                    placeholder="Paste authorization code here..."
                    className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-sm"
                  />
                  <button
                    data-testid="submit-auth-code-btn"
                    onClick={handleConnect}
                    disabled={connecting || !authCode.trim()}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50"
                  >
                    {connecting ? (
                      <RefreshCw size={16} className="animate-spin" />
                    ) : (
                      <Check size={16} />
                    )}
                    {connecting ? "Connecting..." : "Connect"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sync Cards - only show when connected */}
      {isConnected && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SyncCard
            title="Companies"
            icon={Building2}
            syncing={syncingCompanies}
            lastResult={companyResult}
            onSync={() => doSync("companies", setSyncingCompanies, setCompanyResult)}
          />
          <SyncCard
            title="Opportunities"
            icon={Briefcase}
            syncing={syncingOpps}
            lastResult={oppResult}
            onSync={() => doSync("opportunities", setSyncingOpps, setOppResult)}
          />
          <SyncCard
            title="Contacts"
            icon={Users}
            syncing={syncingContacts}
            lastResult={contactResult}
            onSync={() => doSync("contacts", setSyncingContacts, setContactResult)}
          />
        </div>
      )}

      {/* Sync History */}
      {syncHistory.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-semibold flex items-center gap-2 mb-4">
            <History size={18} />
            Sync History
          </h3>
          <div>
            {syncHistory.map((log, i) => (
              <SyncHistoryRow key={i} log={log} />
            ))}
          </div>
        </div>
      )}

      {/* Not Connected - instructions */}
      {!isConnected && (
        <div className="bg-secondary/30 border border-border rounded-xl p-6">
          <h3 className="font-semibold mb-3">How it works</h3>
          <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
            <li>Click Connect to Spiro above to start the OAuth authorization</li>
            <li>Authorize the app in your browser when prompted by Spiro</li>
            <li>Copy the authorization code from the redirect URL and paste it back here</li>
            <li>Once connected, use Sync All to pull all data from Spiro into the database</li>
          </ol>
        </div>
      )}
    </div>
  );
};
